import React, { useState } from 'react'

import DateRangeSelect from '../../components/DateRangeSelect'
import MultiLineChart from '../../components/MultiLineChart'
import MultiLineChartAum from '../../components/MultiLineChartAum'
import StackedAreaChart from '../../components/StackedAreaChart'
import StackedBarChart from '../../components/StackedBarChart'
import ZeroCenteredBarChart from '../../components/ZeroCenteredBarChart'

import useBlpStats from '../../lib/useBlpStats'
import useFeeStats from '../../lib/useFeeStats'
import useOverview from '../../lib/useOverview'
import useTokenStats from '../../lib/useTokenStats'
import useTradingStats from '../../lib/useTradingStats'
import useUserStats from '../../lib/useUserStats'
import useVolumeStats from '../../lib/useVolumeStats'
import './Stats.css'

export const formatNumber = (number, usd = false, scale = true) => {
  if (isNaN(number) || number === '' || number === undefined) {
    return usd ? '$0.00' : '0'
  }
  const num = parseFloat(number)

  // if (num > 500000000 && scale) {
  //   return (usd ? '$' : '') + formatK(num.toFixed(0))
  // }

  if (num === 0) {
    if (usd) {
      return '$0.00'
    }
    return '0'
  }

  if (num < 0.0001 && num > 0) {
    return usd ? '< $0.0001' : '< 0.0001'
  }

  if (num > 1000) {
    return usd
      ? '$' + Number(parseFloat(String(num)).toFixed(0)).toLocaleString()
      : '' + Number(parseFloat(String(num)).toFixed(0)).toLocaleString()
  }

  // if (usd) {
  //   if (num < 0.1) {
  //     return '$' + Number(parseFloat(String(num)).toFixed(4))
  //   } else {
  //     const usdString = priceFormatter.format(num)
  //     return '$' + usdString.slice(1, usdString.length)
  //   }
  // }

  return parseFloat(String(num)).toPrecision(4)
}

export default function Stats() {
  // Date Range parameters config
  const [dataRange, setDataRange] = useState({})

  const onDateRangeChange = (dates) => {
    const [start, end] = dates
    setDataRange({
      fromValue: start,
      toValue: end,
    })
  }

  const overview = useOverview(dataRange)
  const volumeStats = useVolumeStats(dataRange)
  const feeStats = useFeeStats(dataRange)
  const tradingStats = useTradingStats(dataRange)
  const userStats = useUserStats(dataRange)
  const blpStats = useBlpStats(dataRange)
  const tokenStats = useTokenStats(dataRange)

  // Setup charts
  const charts = [
    {
      title: 'Volume',
      type: StackedBarChart,
      data: volumeStats,
      keys: ['margin', 'swap', 'liquidation', 'mint', 'burn'],
      legend: ['Margin', 'Swap', 'Liquidation', 'Mint', 'Burn'],
      cumulative: true,
      format: true,
    },
    {
      title: 'Fees',
      type: StackedBarChart,
      data: feeStats,
      keys: ['margin', 'swap', 'liquidation', 'mint', 'burn'],
      legend: ['Margin', 'Swap', 'Liquidation', 'Mint', 'Burn'],
      cumulative: true,
      format: true,
    },
    {
      title: 'AUM & Supply',
      type: MultiLineChartAum,
      data: blpStats,
      keys: ['supply', 'aum'],
      legend: ['Supply', 'AUM'],
      format: true,
    },
    {
      title: 'Pool Composition',
      type: StackedAreaChart,
      data: tokenStats,
      format: true,
    },
    {
      title: 'BLP Performance',
      type: MultiLineChart,
      data: blpStats,
      keys: ['blpPrice'],
      legend: ['BLP Price'],
      format: true,
    },
    {
      title: 'Unique Actions',
      type: StackedBarChart,
      data: userStats,
      keys: ['cntSwap', 'cntMargin', 'cntMintBurn'],
      legend: ['Swap', 'Margin', 'Mint & Burn'],
      cumulative: true,
      format: false,
    },
    {
      title: 'Open Interest',
      type: StackedBarChart,
      data: tradingStats,
      keys: ['longOpenInterest', 'shortOpenInterest'],
      legend: ['Long Interest', 'Short Interest'],
      colors: ['green', 'red'],
      format: true,
    },
    {
      title: 'Profit & Loss',
      type: ZeroCenteredBarChart,
      data: tradingStats,
      keys: ['profit', 'loss', 'profitCumulative', 'lossCumulative'],
      legend: ['Profit', 'Loss', 'Profit Cumulative', 'Loss Cumulative'],
      colors: ['green', 'red'],
      format: true,
    },
  ]

  return (
    <div className="default-container Stats page-layout">
      <div className="Stats-title-block">
        <div className="Stats-title-icon"></div>
        <div className="Stats-title-content">
          <div className="Stats-date-range">
            <DateRangeSelect
              startDate={dataRange.fromValue}
              endDate={dataRange.toValue}
              onChange={onDateRangeChange}
            />
          </div>
        </div>
      </div>
      <div className="Stats-bar">
        <div className="Stats-cell">
          <div className="Stats-cell-title">Total Volume</div>
          <div className="Stats-cell-value">
            {formatNumber(overview.volume.toFixed(2), true, false)}
          </div>
        </div>
        <div className="Stats-cell">
          <div className="Stats-cell-title">Total Fees</div>
          <div className="Stats-cell-value">
            {formatNumber(overview.fees.toFixed(2), true, false)}
          </div>
        </div>
        <div className="Stats-cell">
          <div className="Stats-cell-title">BLP Supply</div>
          <div className="Stats-cell-value">
            {formatNumber(overview.supply)}
          </div>
        </div>
        <div className="Stats-cell">
          <div className="Stats-cell-title">Open Interest</div>
          <div className="Stats-cell-value">
            {formatNumber(overview.interest.toFixed(2), true, false)}
          </div>
        </div>
        <div className="Stats-cell">
          <div className="Stats-cell-title">Total Users</div>
          <div className="Stats-cell-value">{overview.users}</div>
        </div>
      </div>
      <div className="Stats-content">
        <div className="Stats-cards">
          {charts.map((val, index) => (
            <div key={index} className="App-card">
              <div className="App-card-title">{val.title}</div>
              <div className="App-card-divider"></div>
              <div className="App-card-content">
                <val.type
                  data={val.data}
                  xDataKey="x"
                  yDataKeys={val.keys}
                  legend={val.legend}
                  colors={val.colors}
                  cumulative={val.cumulative}
                  format={val.format}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
